import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { IconApi } from '@components/icons/api';
import { IconApplicants } from '@components/icons/applicants';
import { IconBrandADManager } from '@components/icons/brand-ad-manager';
import { IconBrandAsana } from '@components/icons/brand-asana';
import { IconBrandDokobit } from '@components/icons/brand-dokobit';
import { IconBrandGetAccept } from '@components/icons/brand-getaccept';
import { IconBrandH3 } from '@components/icons/brand-h3';
import { IconBrandHireflix } from '@components/icons/brand-hireflix';
import { IconBrandHRMonitor } from '@components/icons/brand-hrmonitor';
import { IconBrandKjarni } from '@components/icons/brand-kjarni';
import { IconBrandMyTimePlan } from '@components/icons/brand-mytimeplan';
import { IconBrandOneSystems } from '@components/icons/brand-onesystems';
import { IconBrandRelesys } from '@components/icons/brand-relesys';
import { IconBrandSignet } from '@components/icons/brand-signet';
import { IconBrandSlack } from '@components/icons/brand-slack';
import { IconBrandTaktikal } from '@components/icons/brand-taktikal';
import { IconBrandTimon } from '@components/icons/brand-timon';
import { IconCompany } from '@components/icons/company';
import { IconDepartment } from '@components/icons/department';
import { IconTheme } from '@components/icons/icon-theme';
import { IconIntegrations } from '@components/icons/integrations';
import { IconJobboard } from '@components/icons/jobboard';
import { IconJourneyLogoPlain } from '@components/icons/journey-logo-plain';
import { IconLabel } from '@components/icons/label';
import { IconLocation } from '@components/icons/location';
import { IconNotifications } from '@components/icons/notifications';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconPersonOff } from '@components/icons/person-off';
import { IconSettings } from '@components/icons/settings';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconSSO } from '@components/icons/sso';
import { IconTeam } from '@components/icons/team';
import { IconTemplate } from '@components/icons/template';
import { IconWorkType } from '@components/icons/work-type';
import { RelativeSpinner } from '@components/loading-spinner';
import { Nav } from '@components/nav';
import { NavGroupCollapse } from '@components/nav-group-collapse';
import { Link } from '@components/nav-group-collapse/nav-group-collapse';
import { HireNav } from '@components/nav/nav';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';

import { useIntegrationSettings } from 'api/companies/hooks';
import { useFeature } from 'features/authentication/authentication-queries';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

const iconSize = 16;

type LocationState = {
  from: string;
};

function useIntegrationLinks(isLoading: boolean) {
  const hrefPrefix = '/settings/apps';
  const { t } = useScopedTranslate(locale);
  const { hasRolePermission } = usePermissions();
  const hasJourneysIntegration = useFeature('FEATURE_INTEGRATION_JOURNEYS');
  return function getIntegrationLinks(integrations: string[]): Link[] {
    if (isLoading) {
      return [];
    }
    const integrationLinks = [];
    integrationLinks.push({
      id: 'integrations',
      icon: <IconIntegrations size={iconSize} />,
      href: `${hrefPrefix}/integrations`,
      label: t('addIntegrations'),
    });
    if (hasJourneysIntegration && integrations.includes('journeys')) {
      integrationLinks.push({
        id: 'journeys',
        icon: <IconJourneyLogoPlain size={iconSize} />,
        href: `${hrefPrefix}/journeys`,
        label: t('journeys'),
      });
    }
    if (integrations.includes('slack')) {
      integrationLinks.push({
        id: 'slack',
        icon: <IconBrandSlack size={iconSize} />,
        href: `${hrefPrefix}/slack`,
        label: t('slack'),
      });
    }
    if (integrations.includes('getaccept')) {
      integrationLinks.push({
        id: 'get-accept',
        icon: <IconBrandGetAccept size={iconSize} />,
        href: `${hrefPrefix}/getaccept`,
        label: t('getAccept'),
      });
    }
    if (integrations.includes('onboardingForm')) {
      integrationLinks.push({
        id: 'onboarding-form',
        icon: <IconOnboarding size={iconSize} />,
        href: `${hrefPrefix}/onboardingForm`,
        label: t('onboardingForm'),
      });
    }
    if (integrations.includes('kjarni')) {
      integrationLinks.push({
        id: 'kjarni',
        icon: <IconBrandKjarni size={iconSize} />,
        href: `${hrefPrefix}/kjarni`,
        label: t('kjarni'),
      });
    }
    if (integrations.includes('asana')) {
      integrationLinks.push({
        id: 'asana',
        icon: <IconBrandAsana size={iconSize} />,
        href: `${hrefPrefix}/asana`,
        label: t('asana'),
      });
    }
    if (integrations.includes('hrMonitor')) {
      integrationLinks.push({
        id: 'hr-monitor',
        icon: <IconBrandHRMonitor size={iconSize} />,
        href: `${hrefPrefix}/hrmonitor`,
        label: t('hrMonitor'),
      });
    }
    if (integrations.includes('relesys')) {
      integrationLinks.push({
        id: 'relesys',
        icon: <IconBrandRelesys size={iconSize} />,
        href: `${hrefPrefix}/relesys`,
        label: t('relesys'),
      });
    }
    if (integrations.includes('hireflix')) {
      integrationLinks.push({
        id: 'hireflix',
        icon: <IconBrandHireflix size={iconSize} />,
        href: `${hrefPrefix}/hireflix`,
        label: t('hireflix'),
      });
    }
    if (integrations.includes('h3core')) {
      integrationLinks.push({
        id: 'h3core',
        icon: <IconBrandH3 size={iconSize} />,
        href: `${hrefPrefix}/h3core`,
        label: t('h3core'),
      });
    }
    if (integrations.includes('dokobit')) {
      integrationLinks.push({
        id: 'dokobit',
        icon: <IconBrandDokobit size={iconSize} />,
        href: `${hrefPrefix}/dokobit`,
        label: t('dokobit'),
      });
    }
    if (integrations.includes('onecrm')) {
      integrationLinks.push({
        id: 'onecrm',
        icon: <IconBrandOneSystems size={iconSize} />,
        href: `${hrefPrefix}/onecrm`,
        label: t('oneSystems'),
      });
    }
    if (integrations.includes('timon')) {
      integrationLinks.push({
        id: 'timon',
        icon: <IconBrandTimon size={iconSize} />,
        href: `${hrefPrefix}/timon`,
        label: t('timon'),
      });
    }
    if (integrations.includes('mytimeplan')) {
      integrationLinks.push({
        id: 'mytimeplan',
        icon: <IconBrandMyTimePlan size={iconSize} />,
        href: `${hrefPrefix}/mytimeplan`,
        label: t('myTimeplan'),
      });
    }
    if (integrations.includes('adManager')) {
      integrationLinks.push({
        id: 'admanager',
        icon: <IconBrandADManager size={iconSize} />,
        href: `${hrefPrefix}/admanager`,
        label: t('adManager'),
      });
    }
    if (integrations.includes('taktikal') && hasRolePermission('company:view-taktikal')) {
      integrationLinks.push({
        id: 'taktikal',
        icon: <IconBrandTaktikal size={iconSize} />,
        href: `${hrefPrefix}/taktikal`,
        label: t('taktikal'),
      });
    }
    if (integrations.includes('signet') && hasRolePermission('company:view-signet')) {
      integrationLinks.push({
        id: 'signet',
        icon: <IconBrandSignet size={iconSize} />,
        href: `${hrefPrefix}/signet`,
        label: t('signet'),
      });
    }
    return integrationLinks;
  };
}

const expandedStore = createSelectionStore({ persistKey: 'company-settings-collapse' });

const useGroups = () => {
  const { current } = useStore();
  const { t } = useScopedTranslate(locale);
  const { hasRolePermission } = usePermissions();
  const canViewCareerSettings = hasRolePermission('company:view-career-settings');
  const canViewAdminSettings = hasRolePermission('company:view-admin-settings');
  const canViewIntegrationsSettings = hasRolePermission('company:view-integrations-settings');
  const groups = [
    ...(canViewAdminSettings
      ? [
          {
            id: 'adminsettings',
            label: t('adminSettings'),
            links: [
              hasRolePermission('company:details-settings') && {
                id: 'settings-company',
                icon: <IconCompany size={iconSize} />,
                href: '/settings/company',
                label: t('company'),
              },
              hasRolePermission('locations:view') && {
                id: 'settings-locations',
                href: '/settings/locations',
                icon: <IconLocation size={iconSize} />,
                label: t('locations'),
              },
              current.info.isEnterprise && {
                id: 'settings-departments',
                href: '/settings/departments',
                icon: <IconDepartment size={iconSize} />,
                label: t('departments'),
              },
              hasRolePermission('worktypes:view') && {
                id: 'settings-worktypes',
                href: '/settings/worktypes',
                icon: <IconWorkType size={iconSize} />,
                label: t('worktypes'),
              },
              hasRolePermission('team:view') && {
                id: 'settings-team',
                href: '/settings/team',
                icon: <IconTeam size={iconSize} />,
                label: t('users'),
              },
              {
                id: 'settings-templates',
                href: '/settings/templates',
                icon: <IconTemplate size={iconSize} />,
                label: t('templates'),
              },
              hasRolePermission('joblabels:view') && {
                id: 'settings-labels',
                href: '/settings/labels',
                icon: <IconLabel size={iconSize} />,
                label: t('labels'),
              },
              {
                id: 'settings-jobboards',
                href: '/settings/jobboards',
                icon: <IconJobboard size={iconSize} />,
                label: t('jobBoards'),
              },
              {
                id: 'settings-spam-protection',
                href: '/settings/spamProtection',
                icon: <IconPersonOff size={iconSize} />,
                label: t('spamProtection'),
              },
            ],
          },
        ]
      : []),
    ...(canViewIntegrationsSettings
      ? [
          {
            id: 'integrations',
            label: t('integrations'),
            links: [],
          },
        ]
      : []),
    ...(canViewCareerSettings
      ? [
          {
            id: 'career',
            label: t('career'),
            links: [
              {
                icon: <IconSettings size={iconSize} />,
                href: '/settings/career',
                label: t('settings'),
              },
              {
                href: '/settings/theme',
                icon: <IconTheme size={iconSize} />,
                label: t('theme'),
              },
              {
                href: '/settings/jobordering',
                icon: <IconSortDesc size={iconSize} />,
                label: t('jobOrdering'),
              },
            ],
          },
        ]
      : []),
    {
      id: 'account',
      label: t('account'),
      links: [
        {
          id: 'details',
          icon: <IconApplicants size={iconSize} />,
          href: '/settings/profile/details',
          label: t('profile'),
        },
        hasRolePermission('notifications:view') && {
          id: 'notifications',
          icon: <IconNotifications size={iconSize} />,
          href: '/settings/profile/notifications',
          label: t('notifications'),
        },
        !current.info.useForceSso && {
          id: 'security',
          icon: <IconSSO size={iconSize} />,
          href: '/settings/profile/security',
          label: t('security'),
        },
        hasRolePermission('api:view') && {
          id: 'api',
          icon: <IconApi size={iconSize} />,
          href: '/settings/profile/api',
          label: t('api'),
        },
      ],
    },
  ] as {
    id: string;
    label: string;
    links: Array<Link>;
  }[];

  return groups;
};

export const CompanySettingsNavigation = () => {
  const { t } = useScopedTranslate(locale);
  const { hasRolePermission } = usePermissions();
  const location = useLocation();
  const { from } = (location.state as LocationState) || { from: '/' };

  const canViewIntegrationsSettings = hasRolePermission('company:view-integrations-settings');
  const integrationSettings = useIntegrationSettings({ enabled: canViewIntegrationsSettings });
  const getIntegrationLinks = useIntegrationLinks(integrationSettings.isLoading);

  const groups = useGroups();
  const combinedGroups = useMemo(() => {
    if (!integrationSettings.data?.connected) return groups;
    const integrationList = integrationSettings.data.connected;

    return groups.map((group) => {
      if (group.id === 'integrations') {
        return {
          ...group,
          links: getIntegrationLinks(integrationList),
        };
      }
      return group;
    });
  }, [getIntegrationLinks, groups, integrationSettings.data]);

  return (
    <HireNav
      stateStorageKey="company-nav-state"
      variant="static"
      fixedHeader={<Nav.Header logo={<Nav.HeaderBackLink href={from} label={t('back')} />} />}
    >
      {combinedGroups.map((group, index) => {
        const isLastGroup = index === groups.length - 1;
        return (
          <div key={group.id}>
            <NavGroupCollapse
              id={group.id}
              label={group.label}
              links={group.links}
              toggleGroup={expandedStore.toggle}
              expandedSelectionStore={expandedStore}
            />
            {group.id === 'integrations' && integrationSettings.isLoading && <RelativeSpinner />}
            {!isLastGroup && <Nav.Separator />}
          </div>
        );
      })}
    </HireNav>
  );
};

const locale = {
  en: {
    back: 'Back',
    company: 'General',
    adminSettings: 'Organization',
    departments: 'Departments',
    users: 'Users',
    account: 'Account',
    locations: 'Locations',
    worktypes: 'Work types',
    theme: 'Theme',
    career: 'Career site',
    templates: 'Templates',
    labels: 'Labels',
    jobBoards: 'Job Boards',
    integrations: 'Integrations',
    jobOrdering: 'Job Ordering',
    settings: 'Settings',
    hireflix: 'Hireflix',
    getAccept: 'GetAccept',
    onboardingForm: 'Onboarding Form',
    kjarni: 'Kjarni',
    asana: 'Asana',
    hrMonitor: 'HR Monitor',
    relesys: 'Relesys',
    h3core: 'H3 Core',
    dokobit: 'Dokobit',
    oneSystems: 'One Systems',
    timon: 'Timon',
    myTimeplan: 'MyTimeplan',
    taktikal: 'Taktikal',
    signet: 'Signet',
    slack: 'Slack',
    addIntegrations: 'Add integrations',
    adManager: 'ADManager',
    profile: 'Profile',
    notifications: 'Notifications',
    security: 'Security',
    api: 'API Keys',
    journeys: 'Journeys',
    spamProtection: 'Spam protection',
  },
  is: {
    back: 'Til baka',
    company: 'Stillingar',
    adminSettings: 'Fyrirtæki',
    departments: 'Deildir',
    users: 'Notendur',
    account: 'Prófíll',
    locations: 'Staðsetningar',
    worktypes: 'Tegund starfs',
    theme: 'Þema',
    career: 'Ráðningarsíða',
    templates: 'Sniðmát',
    labels: 'Merki',
    jobBoards: 'Starfatorg',
    integrations: 'Tengingar',
    jobOrdering: 'Starfaröðun',
    settings: 'Stillingar',
    hireflix: 'Hireflix',
    getAccept: 'GetAccept',
    onboardingForm: 'Innleiðingarform',
    kjarni: 'Kjarni',
    asana: 'Asana',
    hrMonitor: 'HR Monitor',
    relesys: 'Relesys',
    h3core: 'H3 Core',
    dokobit: 'Dokobit',
    oneSystems: 'One Systems',
    timon: 'Timon',
    myTimeplan: 'MyTimeplan',
    taktikal: 'Taktikal',
    signet: 'Signet',
    slack: 'Slack',
    addIntegrations: 'Tengja samþættingar',
    adManager: 'ADManager',
    profile: 'Prófíll',
    notifications: 'Tilkynningar',
    security: 'Öryggi',
    api: 'API Lyklar',
    journeys: 'Journeys',
    spamProtection: 'Bannlisti',
  },
};
